import { useRef, useEffect, useState } from 'react';
import gsap from 'gsap/all';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { Container, Icon, Text, useTranslation, useUi } from '@weareconceptstudio/core';

//* Helpers
import { config } from '@/helpers';

//* Styles
import ContentStyle from './style';

//* Components
import Button from '@/components/Button';
import Description from '../Description';
import Gallery from './Gallery';
import CustomLink from '@/components/CustomLink';

//* SVG
import { compositionSVG, colorSVG, foodPairingsSVG, noseSVG } from './utils';

gsap.registerPlugin(ScrollTrigger);

const Content = ({ title, description, volume, link, images, composition, food_pairings, color, nosen, infos }) => {
	//! Ref
	const galleryRef = useRef();
	const infoRef = useRef();
	const scrollRef = useRef();
	const heroTopRef = useRef();
	const additionalInfosRef = useRef();
	const textRefInfo = useRef();
	const linkInfoRef = useRef();

	const { winWidth } = useUi();
	const { translate } = useTranslation();

	const [load, setLoad] = useState(false);

	const additionalInfos = [
		{ key: 'composition', data: composition, svg: compositionSVG },
		{ key: 'color', data: color, svg: colorSVG },
		{ key: 'foodPairings', data: food_pairings, svg: foodPairingsSVG },
		{ key: 'nosen', data: nosen, svg: noseSVG },
	];

	useEffect(() => {
		if (galleryRef.current && infoRef.current && heroTopRef.current) {
			if (winWidth > 1024) {
				const endMarker = galleryRef.current.offsetHeight;
				const topMarker = galleryRef.current.offsetTop;

				infoRef.current.style.minHeight = endMarker + 'px';
				scrollRef.current = ScrollTrigger.create({
					start: `top top+=${topMarker}`,
					end: `bottom top+=${endMarker + topMarker}`,
					// markers: true,
					trigger: galleryRef.current,
					pin: true,
					endTrigger: heroTopRef.current,
					pinSpacing: false,
				});
			}
		}

		return () => {
			if (scrollRef.current) {
				scrollRef.current.kill();
			}
			if (infoRef.current) {
				infoRef.current.style.minHeight = 'auto';
			}
		};
	}, [winWidth]);

	useEffect(() => {
		if (additionalInfosRef.current && textRefInfo.current && load) {
			gsap.fromTo(
				[textRefInfo.current.children, ...additionalInfosRef.current.children],
				{
					opacity: 0,
				},
				{ opacity: 1, stagger: 0.1, ease: 'power2.in', delay: 0.3 }
			);

			gsap.fromTo(
				[linkInfoRef.current?.children[0], linkInfoRef.current?.children[1]],
				{
					scale: 0,
					opacity: 0,
				},
				{ opacity: 1, scale: 1, duration: 0.6, ease: 'power2.out', delay: 1, stagger: 0.15 }
			);
		}
	}, [link, load]);
	return (
		<ContentStyle>
			<Container
				row
				isSection>
				<div className='col-6 col-ds-6 col-dx-12'>
					<div
						ref={galleryRef}
						className='wrap-gallery'>
						<Gallery
							loading={setLoad}
							data={images}
						/>
					</div>
				</div>

				<div
					ref={heroTopRef}
					className='hero-top-wrapper col-5 col-ds-6 col-dx-12'>
					<div
						ref={infoRef}
						className={`wrapper-infos ${link ? '' : 'center'}`}>
						<div className='inner-info'>
							<div
								ref={textRefInfo}
								className='hero-top-inner-wrapper main-info-wrapper'>
								<Text className='p1 font-fss-light opacity-zero'>{volume}</Text>

								<Text className='h4 font-larken-light hero-title opacity-zero'>{title}</Text>

								<Text className='p1 font-fss-light opacity-zero'>{description}</Text>
							</div>

							<div
								ref={additionalInfosRef}
								className='additional-info-wrapper'>
								{additionalInfos.map(({ key, data, svg }) => (
									<div
										key={key}
										className='additional-info-item opacity-zero'>
										<div className='additional-info-top-wrapper'>
											{svg}
											<Text
												className='p2 font-fss-regular'
												text={key}
											/>
										</div>
										<Text className='p2 font-fss-light additional-info-description'>{data.text}</Text>
									</div>
								))}
							</div>
						</div>
						<div
							className='link-info-wrapper'
							ref={linkInfoRef}>
							{link ? (
								<div className='button-wrapper opacity-zero'>
									<Button
										btnType={'yellow-type'}
										url={link || '/'}
										text={'buyOnline'}
										external
									/>
								</div>
							) : null}

							<div
								ref={infoRef}
								className='info-wrapper-button opacity-zero'>
								<Icon
									className='icon'
									name={'info'}
								/>
								<Text className='p1 font-fss-light'>
									{translate('orderOutside') + ' '}

									<CustomLink url={config.routes.contactUs.path}>
										<Text
											tag={'span'}
											className='p1 font-fss-regular link-contact'>
											{translate(config.routes.contactUs.name) + '.'}
										</Text>
									</CustomLink>
								</Text>
							</div>
						</div>
					</div>

					<Description items={infos} />
				</div>

				<div className='col-1 col-ds-12 col-dx-12' />
			</Container>
		</ContentStyle>
	);
};

export default Content;
